<script setup lang="ts">
import { logger } from '@/utils/logger';
import { useGaiaToast } from '@/composables/useGaiaToast';
import { computed, ref, watch } from 'vue';
import { getContractStatistics, type ContractStatistics } from '@/api';
import StatisticsKpiGrid, {
  type StatisticsKpiTile,
} from '@/components/admin/StatisticsKpiGrid.vue';
import {
  buildStatisticsDateQuery,
  formatStatisticsNumber,
  type StatisticsFiltersProps,
} from '@/utils/adminStatistics';

const props = defineProps<StatisticsFiltersProps>();
const message = useGaiaToast();

const data = ref<ContractStatistics | undefined>(undefined);
const loading = ref(false);

const kpiTiles = computed<StatisticsKpiTile[]>(() => {
  if (data.value === undefined) {
    return [];
  }

  return [
    { label: 'Total contracts', value: formatStatisticsNumber(data.value.total_contracts) },
    {
      label: 'Avg files / contract',
      value: formatStatisticsNumber(data.value.average_contract_file_count_per_contract),
    },
    {
      label: 'Avg extractions / file',
      value: formatStatisticsNumber(data.value.average_extraction_count_per_contract_file),
    },
    { label: 'Total matters', value: formatStatisticsNumber(data.value.total_matters) },
    {
      label: 'Avg contracts / matter',
      value: formatStatisticsNumber(data.value.average_contract_count_per_matter),
    },
    { label: 'Total user shares', value: formatStatisticsNumber(data.value.total_user_shares) },
    { label: 'Total role shares', value: formatStatisticsNumber(data.value.total_role_shares) },
  ];
});

async function fetchStatistics(): Promise<void> {
  loading.value = true;
  try {
    const response = await getContractStatistics({
      query: {
        included_org_uids: props.includedOrgUids,
        excluded_org_uids: props.excludedOrgUids,
        ...buildStatisticsDateQuery(props.dateRange),
      },
    });
    if (response.response?.status === 200) {
      data.value = response.data;
    }
  } catch (error) {
    message.error('Failed to load contract statistics');
    logger.error('Failed to load contract statistics:', error);
  } finally {
    loading.value = false;
  }
}

watch(props, fetchStatistics, { deep: true, immediate: true });
</script>

<template>
  <n-spin :show="loading">
    <statistics-kpi-grid v-if="data !== undefined" :tiles="kpiTiles" />
    <div v-else style="min-height: 200px" />
  </n-spin>
</template>

<style scoped></style>
